import { IAgentLayoutProps } from "./util";
import React from "react";
import { Carousel } from "../Carousel";
import ContactCard, {
  ECardIconsLayout,
  EContactCardLayout,
} from "./ContactCard";
import { ContactForm } from "../Forms";
import { Search } from "../Search";
import {
  EListingCardLayouts,
  EListingVitalsLayout,
  ListingCard,
} from "../Listing";
import { BackgroundImage } from "../Media";
import TabbedContentBlade, { ITabContent } from "../Blades/TabbedContentBlade";
import Testimonials from "../Blades/Testimonials";

export const AgentLayoutListingHeader: React.FC<IAgentLayoutProps> = ({
  className = "",
  agent,
  listings,
  listingsTabsProps,
  carouselProps,
  carouselListingCardVitalsLayout,
  testimonialsProps,
  showRequestInfoForm,
  linkFunc,
  linkImage,
  requestInfoFormProps,
  requestInfoFormBG,
  showContactUsForm,
  contactUsFormProps,
  contactUsFormBG,
  contactCardProps,
  contactCardBG,
  tabsListingCardVitalsLayout,
  showCarousel = true,
  showAgentBioReadMoreButton = true,
  heroImage,
  marketDescription,
  agentVideo,
  agentVideoEmbed,
}) => {
  let requestInfo = showRequestInfoForm && (
    <ContactForm {...requestInfoFormProps} />
  );
  requestInfo &&
    requestInfoFormBG &&
    (requestInfo = (
      <BackgroundImage src={requestInfoFormBG.url}>
        {requestInfo}
      </BackgroundImage>
    ));

  let contactUs = showContactUsForm && <ContactForm {...contactUsFormProps} />;
  contactUs &&
    contactUsFormBG &&
    (contactUs = (
      <BackgroundImage src={contactUsFormBG.url}>{contactUs}</BackgroundImage>
    ));
  let tabPanels: ITabContent[] = [];

  listings &&
    listings.active &&
    listings.active.length > 0 &&
    tabPanels.push({
      contentClassName: "w-full",
      contentId: "activelistings",
      tabTitle: listingsTabsProps?.activeListingsTabTitle ?? "Residential",
      tabId: "activellistingstab",
      content: (
        <Search
          index={listings.active}
          pageSize={0}
          renderItem={(item, index) => (
            <ListingCard
              listing={item}
              layout={EListingCardLayouts.vertical}
              className="w-[580px] max-w-full"
              maxWidthImage={580}
              vitalsLayout={EListingVitalsLayout.column2Border1}
              key={index}
              keyIndex={index}
              linkFunc={linkFunc}
              linkImage={linkImage}
              {...tabsListingCardVitalsLayout}
            />
          )}
        />
      ),
    });

  listings &&
    listings.sold &&
    listings.sold.length > 0 &&
    tabPanels.push({
      contentClassName: "w-full",
      contentId: "soldlistings",
      tabTitle: listingsTabsProps?.soldListingsTabTitle ?? "Recently Sold",
      tabId: "soldlistingstab",
      content: (
        <Search
          index={listings.sold}
          pageSize={0}
          renderItem={(item, index) => (
            <ListingCard
              listing={item}
              layout={EListingCardLayouts.vertical}
              className="w-[580px] max-w-full"
              maxWidthImage={580}
              vitalsLayout={EListingVitalsLayout.column2Border1}
              key={index}
              keyIndex={index}
              linkFunc={linkFunc}
              linkImage={linkImage}
              {...tabsListingCardVitalsLayout}
            />
          )}
        />
      ),
    });

  const testimonialsRightCol = (
    <>
      <h1 className="text-left">
        Working with:
        <br />
        <span className={`text-6xl`}>{agent.first}</span>
      </h1>
    </>
  );

  let contactCard = agent && (
    <ContactCard
      contact={agent}
      layout={EContactCardLayout.pageSection}
      contactInfoItemsLayout={ECardIconsLayout.columns}
      className="max-w-screen-xl mx-auto"
      iconAccent={true}
      showAddress={true}
      showBio={true}
      showVCard={false}
      imageClasses="md:object-contain md:object-top lg:object-cover"
      showAgentBioReadMoreButton={showAgentBioReadMoreButton}
      {...contactCardProps}
    />
  );

  if (contactCard) {
    if (contactCardBG) {
      contactCard = (
        <BackgroundImage
          src={contactCardBG.url}
          className="bg-no-repeat bg-cover"
        >
          {contactCard}
        </BackgroundImage>
      );
    } else {
      contactCard = <div className="inverted">{contactCard}</div>;
    }
  }

  return (
    <div className={`${className} agent-layout my-0 mx-auto`}>
      {showCarousel && listings && listings.active?.[0] && (
        <Carousel {...carouselProps}>
          {listings.active.map((listing, index) => (
            <ListingCard
              layout={EListingCardLayouts.composite}
              vitalsLayout={
                carouselListingCardVitalsLayout ??
                EListingVitalsLayout.column1Border1Alt2
              }
              vitalsClassName="justify-center"
              vitalsLocationIcon={false}
              listing={listing}
              className=""
              maxWidthImage={2400}
              linkFunc={linkFunc}
              key={index}
              imageProps={{
                imgProps: {
                  className:
                    "w-full h-auto lg:h-full object-cover object-center aspect-[3/4] md:aspect-auto",
                  wrapperClassName: "block w-full lg:h-screen lg:max-h-screen",
                  wrapperProps: {
                    style: { display: "block" },
                  },
                },
              }}
            />
          ))}
        </Carousel>
      )}

      {!listings?.active?.[0] && heroImage?.url && (
        <BackgroundImage
          src={heroImage.url}
          className="w-full lg:h-screen lg:max-h-screen"
        ></BackgroundImage>
      )}

      {contactCard}

      {requestInfo}

      {agentVideo?.url && (
        <section className="relative max-w-screen-xl w-full mx-auto">
          <video
            className="w-full aspect-[16/9]"
            controls
            playsInline
            src={`${agentVideo.url}#t=0.001`}
          ></video>
        </section>
      )}

      {agentVideoEmbed && (
        <iframe
          className="relative max-w-screen-xl w-full mx-auto aspect-[16/9]"
          src={agentVideoEmbed}
        ></iframe>
      )}

      {marketDescription && (
        <div
          className="market-description max-w-screen-xl mx-auto p-2 lg:py-10"
          dangerouslySetInnerHTML={{ __html: marketDescription }}
        ></div>
      )}

      {testimonialsProps && testimonialsProps.testimonials && (
        <Testimonials
          leftColumn={testimonialsRightCol}
          showAttribution={false}
          className="inverted"
          {...testimonialsProps}
        />
      )}

      {tabPanels.length > 0 && (
        <TabbedContentBlade
          title={listingsTabsProps?.title ?? "Listings"}
          titleClassName={listingsTabsProps?.titleClassName}
          tabs={tabPanels}
          className={`w-full max-w-screen-xl mx-auto ${listingsTabsProps?.className}`}
          tabListItemClassNames={
            listingsTabsProps && {
              active: listingsTabsProps?.activeTabClass,
              default: listingsTabsProps?.defaultTabClass,
            }
          }
        />
      )}

      {contactUs}
    </div>
  );
};
